import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import OrdensProducao from './pages/OrdensProducao';
import ChaoFabrica from './pages/ChaoFabrica';
import Maquinarios from './pages/Maquinarios';
import Manutencao from './pages/Manutencao';
import Producao from './pages/Producao';
import Login from './pages/Login';
import TrocarSenhaPrimeiroAcesso from './pages/TrocarSenhaPrimeiroAcesso';
import useAuthStore from './stores/useAuthStore';

// Telas só de admin carregadas sob demanda (12/08/2026, investigação de
// performance) — operador nunca acessa nenhuma delas (vai direto pra
// /producao), mas antes eram baixadas por igual no mesmo bundle único,
// incluindo o recharts inteiro do Dashboard. Telas operacionais (Produção,
// Chão de Fábrica, Ordens, Máquinas, Manutenção) continuam eager — qualquer
// perfil pode usar. Alertas removido do menu/rotas (24/08/2026, pedido do
// usuário) — página/backend continuam no projeto, só não são mais acessíveis
// pela UI.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const Equipe = lazy(() => import('./pages/Equipe'));
const GestaoUsuarios = lazy(() => import('./pages/GestaoUsuarios'));
// Planejamento diario desativado temporariamente para a implantacao em producao.
// const PlanejamentoDiario = lazy(() => import('./pages/PlanejamentoDiario'));

function CarregandoTela() {
    return <div className="flex items-center justify-center h-full py-20 text-anthracite-500 text-sm">Carregando...</div>;
}

function RotaProtegida({ children, moduloId }) {
    const { operadorLogado, temPermissao } = useAuthStore();

    if (!operadorLogado) {
        return <CarregandoTela />;
    }

    // Troca de senha obrigatória (29/08/2026, pedido do usuário) — bloqueia
    // QUALQUER rota protegida até o funcionário definir a senha dele
    // (conta nova ou senha resetada/forçada pelo admin, sempre temporária).
    // Fica antes da checagem de permissão de propósito: não faz sentido
    // checar módulo nenhum enquanto isso não resolver.
    if (operadorLogado.deveTrocarSenha) {
        return <TrocarSenhaPrimeiroAcesso />;
    }

    if (moduloId && !temPermissao(moduloId)) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-surface-3 flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-anthracite-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>
                <h2 className="text-lg font-bold text-anthracite-200 mb-1">Acesso negado</h2>
                <p className="text-anthracite-500 text-[13px]">Você não tem permissão para acessar este módulo.</p>
            </div>
        );
    }

    return children;
}

export default function App() {
    const { operadorLogado, login } = useAuthStore();

    // Apresentação (18/09/2026, pedido do usuário): sem usuário e senha. O
    // mockup entra sozinho como admin (credencial do mock em data/usuarios.json).
    useEffect(() => {
        if (!operadorLogado) void login('admin', '123456');
    }, [operadorLogado, login]);

    return (
        <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
            <Suspense fallback={<CarregandoTela />}>
                <Routes>
                    <Route
                        path="/login"
                        element={operadorLogado ? <Navigate to={operadorLogado.perfil === 'operador' ? '/producao' : '/'} replace /> : <Login />}
                    />

                    <Route path="/" element={
                        <RotaProtegida>
                            <MainLayout />
                        </RotaProtegida>
                    }>
                        {/* Operador (perfil de chão de fábrica): sem menu lateral, cai direto
                            na tela de Produção — evita levar pro Painel (fora do escopo dele). */}
                        <Route index element={
                            operadorLogado?.perfil === 'operador'
                                ? <Navigate to="/producao" replace />
                                : <RotaProtegida moduloId="dashboard"><Dashboard /></RotaProtegida>
                        } />
                        <Route path="chao-fabrica" element={<RotaProtegida moduloId="chao-fabrica"><ChaoFabrica /></RotaProtegida>} />
                        <Route path="ordens"       element={<RotaProtegida moduloId="ordens"><OrdensProducao /></RotaProtegida>} />
                        <Route path="producao"     element={<RotaProtegida moduloId="producao"><Producao /></RotaProtegida>} />
                        {/* Planejamento diario desativado temporariamente para a implantacao em producao. */}
                        {/* <Route path="planejamento" element={<RotaProtegida moduloId="planejamento"><PlanejamentoDiario /></RotaProtegida>} /> */}
                        <Route path="maquinarios"  element={<RotaProtegida moduloId="maquinarios"><Maquinarios /></RotaProtegida>} />
                        <Route path="manutencao"   element={<RotaProtegida moduloId="manutencao"><Manutencao /></RotaProtegida>} />
                        <Route path="relatorios"   element={<RotaProtegida moduloId="relatorios"><Relatorios /></RotaProtegida>} />
                        <Route path="equipe"       element={<RotaProtegida moduloId="equipe"><Equipe /></RotaProtegida>} />
                        <Route path="configuracoes" element={<RotaProtegida moduloId="configuracoes"><Configuracoes /></RotaProtegida>} />
                        <Route path="usuarios"     element={<RotaProtegida moduloId="usuarios"><GestaoUsuarios /></RotaProtegida>} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Suspense>
        </BrowserRouter>
    );
}
